import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, Home } from 'lucide-react';

const Navbar = ({ transparent = false }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = React.useRef(0);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      if (transparent) setScrolled(currentY > 40);
      setHidden(currentY > 200 && currentY > lastScrollY.current);
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [transparent]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleNavigation = (path) => {
    navigate(path);
    closeMenu();
    setShowUserMenu(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setShowUserMenu(false);
    navigate('/');
  };

  const isTransparent = transparent && !scrolled;

  return (
    <nav
      className={`px-4 md:px-12 lg:px-24 py-4 fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        hidden ? '-translate-y-full' : 'translate-y-0'
      } ${
        isTransparent
          ? 'bg-white/5 backdrop-blur-sm border-b border-white/10'
          : 'bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => handleNavigation('/')}
          data-testid="logo"
        >
          <img src="/proptick-logo.png" alt="PropTick Logo" className={`h-10 md:h-12 transition-all duration-300 ${isTransparent ? 'brightness-0 invert' : ''}`} />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={() => navigate('/properties')}
            className={`h-10 px-4 rounded-full font-medium transition-all ${
              isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
            data-testid="nav-browse"
          >
            Explore
          </button>
          <button
            onClick={() => navigate('/list-property')}
            className={`h-10 px-4 rounded-full font-medium transition-all ${
              isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
            data-testid="nav-list-property"
          >
            List Property
          </button>
          
          {user ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 h-10 px-4 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all"
                data-testid="nav-user-menu"
              >
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
                  {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="max-w-[120px] truncate">{user.name || user.email?.split('@')[0] || 'User'}</span>
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="font-medium text-slate-900 truncate">{user.name || user.email}</p>
                    {user.name && <p className="text-sm text-slate-500 truncate">{user.email}</p>}
                  </div>
                  <button
                    onClick={() => handleNavigation(user.role === 'admin' ? '/admin' : '/dashboard')}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-all"
                    data-testid="menu-dashboard"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    <span>{user.role === 'admin' ? 'Admin Dashboard' : 'My Dashboard'}</span>
                  </button>
                  {user.role !== 'admin' && (
                    <button
                      onClick={() => handleNavigation('/list-property')}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-all"
                      data-testid="menu-list-property"
                    >
                      <Home className="w-5 h-5" />
                      <span>List Property</span>
                    </button>
                  )}
                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-all"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className={`flex items-center gap-2 h-10 px-5 rounded-full font-medium transition-all ${
                isTransparent ? 'bg-white/15 text-white border border-white/30 hover:bg-white/25 backdrop-blur-sm' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              data-testid="nav-login"
            >
              <User className="w-5 h-5" />
              <span>Login</span>
            </button>
          )}
        </div>

        {/* Mobile Hamburger Button */}
        <button
          className={`md:hidden p-2 rounded-lg transition-all ${isTransparent ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
          onClick={toggleMenu}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <X className={`w-6 h-6 ${isTransparent ? 'text-white' : 'text-slate-700'}`} />
          ) : (
            <Menu className={`w-6 h-6 ${isTransparent ? 'text-white' : 'text-slate-700'}`} />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 shadow-lg z-40" data-testid="mobile-menu">
          <div className="flex flex-col p-4 gap-2">
            {user && (
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg mb-2">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{user.name}</p>
                  <p className="text-sm text-slate-500">{user.email}</p>
                </div>
              </div>
            )}
            <button
              onClick={() => handleNavigation('/browse')}
              className="w-full h-12 px-4 rounded-lg text-slate-700 hover:bg-slate-100 font-medium transition-all text-left"
              data-testid="mobile-nav-browse"
            >
              Explore Properties
            </button>
            <button
              onClick={() => handleNavigation('/list-property')}
              className="w-full h-12 px-4 rounded-lg text-slate-700 hover:bg-slate-100 font-medium transition-all text-left"
              data-testid="mobile-nav-list-property"
            >
              List Property
            </button>
            {user ? (
              <>
                <button
                  onClick={() => handleNavigation(user.role === 'admin' ? '/admin' : '/dashboard')}
                  className="w-full h-12 px-4 rounded-lg text-slate-700 hover:bg-slate-100 font-medium transition-all text-left"
                  data-testid="mobile-nav-dashboard"
                >
                  {user.role === 'admin' ? 'Admin Dashboard' : 'My Dashboard'}
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full h-12 px-4 rounded-lg bg-red-50 text-red-600 font-medium hover:bg-red-100 transition-all text-center mt-2"
                  data-testid="mobile-nav-logout"
                >
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() => handleNavigation('/login')}
                className="w-full h-12 px-4 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all text-center mt-2"
                data-testid="mobile-nav-login"
              >
                Login / Register
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
